import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api, mediaUrl } from "../lib/api";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Loader2, ExternalLink } from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";
import { useReadTheme } from "../lib/useReadTheme";
import { ReadThemeToggle } from "../components/ReadThemeToggle";
import { SocialPosts } from "../components/articles/SocialPosts";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [theme, , toggle] = useReadTheme();
  const isLight = theme === "light";

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "Article not found" : "Failed to load article"))
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
    catch { return ""; }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-aubergine flex items-center justify-center">
        <Navbar />
        <Loader2 className="h-6 w-6 animate-spin text-champagne" />
      </main>
    );
  }

  if (error || !article) {
    return (
      <main className="min-h-screen bg-aubergine" data-testid="article-not-found">
        <Navbar />
        <div className="pt-32 pb-20 mx-auto max-w-3xl px-6 text-center">
          <p className="font-serif text-3xl text-bone">{error || "Article not found"}</p>
          <Link to="/articles" className="mt-6 inline-flex items-center text-champagne hover:text-champ-light font-mono text-xs uppercase tracking-wider">
            <ArrowLeft className="mr-2 h-3.5 w-3.5" /> Back to articles
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  // Per-tone colour tokens for the surrounding chrome (title, eyebrows, date,
  // "Back to articles" link). The actual body content is themed by `.read-light`
  // on the wrapper, which the existing `.prose-trex` overrides pick up.
  const surface = isLight ? "bg-bone read-light" : "bg-aubergine";
  const backLink = isLight
    ? "text-champagne hover:text-aubergine"
    : "text-champagne hover:text-champ-light";
  const categoryColor = isLight ? "text-champagne" : "text-champagne";
  const titleColor = isLight ? "text-aubergine" : "text-bone";
  const metaColor = isLight ? "text-aubergine/55" : "text-bone/55";

  return (
    <main className={`min-h-screen ${surface}`} data-testid="article-detail-page">
      <Helmet>
        <title>{`${article.seo_title || article.title} | T-REX Network`}</title>
        <meta name="description" content={article.seo_description || article.excerpt || ""} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || ""} />
        {article.cover_image && <meta property="og:image" content={mediaUrl(article.cover_image)} />}
        <meta property="og:type" content="article" />
      </Helmet>
      <Navbar />

      <article className="pt-24 pb-20 lg:pt-32 lg:pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 mb-8">
            <Link to="/articles" className={`inline-flex items-center font-mono text-[11px] uppercase tracking-[0.15em] ${backLink}`} data-testid="article-back-link">
              <ArrowLeft className="mr-2 h-3.5 w-3.5" /> All articles
            </Link>
            <ReadThemeToggle theme={theme} onToggle={toggle} data-testid="article-theme-toggle" />
          </div>
          <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${categoryColor} mb-4`}>{article.category}</p>
          <h1 className={`trex-h1 leading-[1.1] ${titleColor}`}>{article.title}</h1>
          <p className={`mt-6 font-mono text-[11px] uppercase tracking-[0.15em] ${metaColor}`}>
            {formatDate(article.published_at || article.created_at)}
          </p>
          {article.featured_members?.length > 0 && (
            <FeaturedMembersStrip members={article.featured_members} isLight={isLight} />
          )}
          {article.cover_image && (
            <div className="mt-10 rounded-md overflow-hidden">
              <img src={mediaUrl(article.cover_image)} alt={article.title} className="w-full h-auto" />
            </div>
          )}

          <div
            className="mt-10 prose-trex"
            data-testid="article-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.content) }}
          />

          {article.social_links?.length > 0 && (
            <SocialPosts links={article.social_links} isLight={isLight} />
          )}
        </div>
      </article>

      <Footer />
    </main>
  );
}

function FeaturedMembersStrip({ members, isLight }) {
  const eyebrow = isLight ? "text-champagne" : "text-champagne";
  const chipBase = isLight
    ? "inline-flex items-center gap-2 rounded-full border border-aubergine/15 bg-bone-soft hover:border-champagne/60 hover:bg-bone transition-colors pl-1.5 pr-3 py-1"
    : "inline-flex items-center gap-2 rounded-full border border-champ-light/15 bg-aubergine-tier hover:border-champagne/40 hover:bg-aubergine-raised transition-colors pl-1.5 pr-3 py-1";
  const chipText = isLight ? "text-aubergine" : "text-bone";
  const extIconColor = isLight ? "text-aubergine/45" : "text-bone/45";
  const initialsBg = isLight
    ? "h-5 w-5 rounded bg-aubergine/8 flex items-center justify-center font-serif text-[9px] text-champagne italic flex-shrink-0"
    : "h-5 w-5 rounded bg-aubergine-raised flex items-center justify-center font-serif text-[9px] text-champ-light/70 italic flex-shrink-0";

  return (
    <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="featured-members-strip">
      <p className={`font-mono text-[10px] uppercase tracking-[0.2em] whitespace-nowrap ${eyebrow}`}>
        Featured in this story
      </p>
      <div className="flex flex-wrap gap-2">
        {members.map((m) => {
          const inner = (
            <>
              {m.logo_media_id ? (
                <span className="h-5 w-5 rounded bg-bone flex items-center justify-center overflow-hidden p-0.5 flex-shrink-0">
                  <img src={mediaUrl(m.logo_media_id)} alt="" className="max-h-full max-w-full object-contain" />
                </span>
              ) : (
                <span className={initialsBg}>
                  {m.name.slice(0, 2).toUpperCase()}
                </span>
              )}
              <span className={`text-sm ${chipText}`}>{m.name}</span>
              {m.website_url && <ExternalLink className={`h-3 w-3 ${extIconColor}`} />}
            </>
          );
          if (m.website_url) {
            return (
              <a
                key={m.member_id}
                href={m.website_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`featured-member-${m.member_id}`}
                className={chipBase}
              >
                {inner}
              </a>
            );
          }
          return (
            <Link
              key={m.member_id}
              to="/ecosystem"
              data-testid={`featured-member-${m.member_id}`}
              className={chipBase}
            >
              {inner}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
